/**
 * Config-driven announcement system rendered as banners in the dashboard.
 *
 * Devs and product marketing add entries here; CI/CD deploys them automatically.
 * Each announcement is date-filtered and optionally dismissible (persisted in localStorage).
 * For full release notes, link to the Docusaurus blog at docs.api.rpiprint.com/blog.
 *
 * Marketing requests announcements via a Jira ticket — a dev then adds the entry here.
 */

export type AnnouncementVariant = "warning" | "info";

export interface Announcement {
  /** Unique stable ID — convention: {topic}-{yyyy}. Used as localStorage key for dismissal. */
  id: string;
  /** Visual style: "warning" (yellow, default) or "info" (blue). */
  variant?: AnnouncementVariant;
  /** Short message displayed in the announcement banner. */
  message: string;
  /** Optional link. Use `href` for external URLs, `to` for internal routes. */
  link?: { label: string; href?: string; to?: string };
  /** ISO date string (YYYY-MM-DD). Banner is visible on or after this date. Omit for runtime announcements. */
  startDate?: string;
  /** ISO date string (YYYY-MM-DD). Banner is hidden after this date. Omit for indefinite. */
  endDate?: string;
  /** Whether the user can dismiss this announcement. Defaults to true. */
  dismissible?: boolean;
}

// ---------------------------------------------------------------------------
// Active announcements — add new entries at the top
// ---------------------------------------------------------------------------

export const announcements: Announcement[] = [
  // Example — ID convention: {topic}-{yyyy} (e.g., "sandbox-deprecation-2026")
  // {
  //   id: "sandbox-deprecation-2026",
  //   variant: "warning",
  //   message: "The Sandbox environment will be deprecated on May 5th, 2026.",
  //   link: { label: "Learn more", href: "https://docs.api.rpiprint.com/blog/sandbox-deprecation" },
  //   startDate: "2026-03-01",
  //   endDate: "2026-05-05",
  //   dismissible: false,
  // },
];

// ---------------------------------------------------------------------------
// Preview mode — when the previewAnnouncements feature flag is on, all entries
// from the announcements array are shown regardless of date range or
// dismissal state. Sample announcements are included so there's always
// something visible even when the array is empty.
// ---------------------------------------------------------------------------

import { featureFlags } from "./featureFlags";

const sampleAnnouncements: Announcement[] = [
  {
    id: "sample-warning",
    variant: "warning",
    message: "[Sample] Warning banner — dismissible with link.",
    link: { label: "Learn more", href: "https://docs.api.rpiprint.com/blog/welcome" },
    startDate: "2020-01-01",
  },
  {
    id: "sample-info",
    variant: "info",
    message: "[Sample] Info banner — non-dismissible.",
    startDate: "2020-01-01",
    dismissible: false,
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DISMISS_KEY_PREFIX = "announcement_dismissed_";

export function isNotDismissed(a: Announcement): boolean {
  return a.dismissible === false || !isDismissed(a.id);
}

export function filterActiveAnnouncements(entries: Announcement[]): Announcement[] {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(
    2,
    "0"
  )}`;

  return entries.filter((a) => {
    if (a.startDate && a.startDate > today) return false;
    if (a.endDate && a.endDate < today) return false;
    return isNotDismissed(a);
  });
}

/** When previewAnnouncements is on, bypasses date filtering but still respects dismissal. */
export function getActiveAnnouncements(): Announcement[] {
  const entries = featureFlags.previewAnnouncements ? [...announcements, ...sampleAnnouncements] : announcements;

  if (featureFlags.previewAnnouncements) {
    return entries.filter(isNotDismissed);
  }
  return filterActiveAnnouncements(entries);
}

export function isDismissed(id: string): boolean {
  return localStorage.getItem(`${DISMISS_KEY_PREFIX}${id}`) === "true";
}

export function dismissAnnouncement(id: string): void {
  localStorage.setItem(`${DISMISS_KEY_PREFIX}${id}`, "true");
}
